import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import Label from '../Label';
import { cn } from '../../../../utils/styles';

const inputVariants = cva(
  [
    'flex h-[44px] w-full rounded-lg border border-input px-3 py-1 text-sm text-primary-text ',
    'transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium ',
    'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 ',
    'focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 bg-inputBg ',
  ],
  {
    variants: {
      variant: {
        base: 'text-primary-text hover:text-addition-2',
        transparent: '!bg-transparent',
        white: '!bg-white',
        guidance: '!bg-white !border-addition1-20',
        search: '!bg-white !border-addition1-20',
        error: '!border-destructive focus-visible:!ring-destructive',
        home: 'text-white border-[#FFFFFF66] focus-visible:none placeholder:text-[#FFFFFF80]',
        floating: '!bg-white !border-addition1-20',
      },
      sizeVariant: {
        sm: 'h-[32px] rounded-lg px-2 py-0.5 !text-sm',
        default: 'h-[44px]',
        md: 'h-[37px]',
        lg: 'h-[50px] text-base',
        auto: 'h-auto',
        floating: 'h-[56px]',
      },
      iconPosition: {
        left: 'pl-[37px]',
      },
    },
    defaultVariants: {
      variant: 'base',
      sizeVariant: 'default',
    },
  },
);

export interface IInputProps extends React.InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof inputVariants> {
  error?: string | null;
  label?: string;
  icon?: React.ReactNode;
  helper?: string | React.ReactNode;
  inputClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  showPasswordToggle?: boolean;
}

// Вынесенный компонент кнопки переключения пароля
interface PasswordToggleButtonProps {
  showPassword: boolean;
  onToggle: () => void;
}

function PasswordToggleButton({ showPassword, onToggle }: PasswordToggleButtonProps) {
  return (
    <button type="button" onClick={onToggle} className="p-1 hover:bg-gray-100 rounded transition-colors" tabIndex={-1}>
      {showPassword ? <Eye className="text-gray-400" /> : <EyeOff className="text-gray-400" />}
    </button>
  );
}

const Input = React.forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      className,
      type: initialType,
      icon,
      label,
      name,
      prefix,
      error,
      variant,
      iconPosition,
      sizeVariant,
      inputClassName,
      labelClassName,
      errorClassName,
      showPasswordToggle = false,
      ...props
    },
    ref,
  ) => {
    const isFloating = variant === 'floating';
    const [isFocused, setIsFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [hasValue, setHasValue] = useState((props.value !== undefined && props.value !== '') || !!props.defaultValue);

    useEffect(() => {
      setHasValue((props.value !== undefined && props.value !== '') || !!props.defaultValue);
    }, [props.value, props.defaultValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(e.target.value.length > 0);
      props.onChange?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      props.onBlur?.(e);
    };

    const isLabelFloating = isFocused || hasValue;

    const isPassword = initialType === 'password';

    const getInputType = () => {
      if (!isPassword || !showPasswordToggle) {
        return initialType;
      }
      if (showPassword) {
        return 'text';
      }

      return 'password';
    };

    const type = getInputType();

    const getPlaceholder = () => {
      if (isFloating) return undefined;
      if (variant === 'guidance') return '';

      return 'Text input';
    };

    const getPrefixClasses = () => {
      const baseClasses = 'absolute right-3 h-full flex top-0 items-center text-placeholder gap-2 max-w-[50px]';

      if (prefix && variant === 'guidance') {
        return cn(baseClasses, 'left-[12px] right-auto h-auto top-[5px] whitespace-nowrap');
      }

      if (prefix && variant === 'guidance' && !props.value?.toString()?.length) {
        return cn(baseClasses, 'top-0 h-full');
      }

      if (iconPosition === 'left') {
        return cn(
          baseClasses,
          'left-[10px] right-auto opacity-60',
          'before:absolute before:h-[20px] before:w-[1px] before:bg-primary-60 before:right-[-10px]',
        );
      }

      return baseClasses;
    };

    const handleTogglePassword = () => {
      setShowPassword(prev => !prev);
    };

    const renderRightIcon = () => {
      if (isPassword && showPasswordToggle) {
        return (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <PasswordToggleButton showPassword={showPassword} onToggle={handleTogglePassword} />
          </div>
        );
      }

      if (icon) {
        return (
          <div
            className={cn('absolute right-3 h-full flex top-0 items-center text-placeholder gap-2 max-w-[50px]', {
              'top-1/2 -translate-y-1/2 h-auto': isFloating,
            })}
          >
            {icon}
          </div>
        );
      }

      return null;
    };

    return (
      <div className={cn(className)}>
        {label && !isFloating && (
          <Label htmlFor={name} className={cn({ '!text-destructive': error?.length }, labelClassName)}>
            {label}
          </Label>
        )}
        <div className="relative">
          {isFloating && label && (
            <label
              htmlFor={name}
              className={cn(
                'absolute left-3 transition-all duration-200 pointer-events-none origin-left',
                isLabelFloating
                  ? 'top-2 text-[11px] text-muted-foreground scale-100'
                  : 'top-1/2 -translate-y-1/2 text-base text-placeholder scale-100',
                error?.length && (isLabelFloating ? 'text-destructive' : 'text-destructive/70'),
                labelClassName,
              )}
            >
              {label}
            </label>
          )}

          <input
            type={type}
            id={name}
            className={cn(
              inputVariants({
                variant,
                sizeVariant,
                iconPosition,
                className: inputClassName,
              }),
              {
                'pt-[17px]': variant === 'guidance' && props.value?.toString()?.length,
                'border-destructive': error?.length,
                'pl-[37px]': iconPosition === 'left',
                'pt-5 pb-1': isFloating && isLabelFloating,
                'pt-0': isFloating && !isLabelFloating,
                'pr-10': isPassword && showPasswordToggle,
              },
            )}
            placeholder={getPlaceholder()}
            ref={ref}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          />

          {prefix && !isFloating && (
            <div className={getPrefixClasses()}>
              <div className="text-[13px]">{prefix}</div>
            </div>
          )}

          {renderRightIcon()}
        </div>

        {error?.length && (
          <div className={cn('mt-[2px] text-destructive text-[13px] text-right', errorClassName)}>{error}</div>
        )}
      </div>
    );
  },
);

Input.defaultProps = {
  error: undefined,
  label: '',
  prefix: undefined,
  icon: undefined,
  helper: undefined,
  variant: 'base',
  sizeVariant: 'default',
  inputClassName: undefined,
  labelClassName: undefined,
  errorClassName: undefined,
  showPasswordToggle: false,
};

Input.displayName = 'Input';

export { Input };
